/**
 * Enhanced HS Code Search with UoM Validation
 * Provides HS Code search with automatic UoM validation and suggestions
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api, { apiClient } from '../services/api';

const HSCodeSearchWithUoM = ({
  onHSCodeSelect,
  onUoMChange,
  currentHSCode,
  currentUoM,
  disabled = false,
  showUoMValidation = true
}) => {
  const [searchTerm, setSearchTerm] = useState(currentHSCode || '');
  const [hsCodes, setHsCodes] = useState([]);
  const [allHsCodes, setAllHsCodes] = useState([]); // For fallback search
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setSelectedHSCode] = useState(null);

  // UoM validation states
  const [, setUoMOptions] = useState([]);
  const [, setUoMValidation] = useState(null);
  const [, setLoadingUoM] = useState(false);
  const [, setShowUoMSuggestions] = useState(false);

  // FBR API integration states
  const [fbrUomData, setFbrUomData] = useState(null);
  const [loadingFbrUom, setLoadingFbrUom] = useState(false);
  const [fbrUomError, setFbrUomError] = useState(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const onUoMChangeRef = useRef(onUoMChange);
  const currentUoMRef = useRef(currentUoM);

  useEffect(() => {
    onUoMChangeRef.current = onUoMChange;
  }, [onUoMChange]);

  useEffect(() => {
    currentUoMRef.current = currentUoM;
  }, [currentUoM]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowUoMSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load all HS codes for fallback search
  const loadAllHSCodes = useCallback(async () => {
    try {
      const response = await api.reference.getHSCodes({ limit: 1000 });
      setAllHsCodes(response.data.data || []);
      console.log(`📋 Loaded ${response.data.data?.length || 0} HS codes for fallback search`);
    } catch (error) {
      console.warn('Failed to load all HS codes for fallback:', error);
      // Use static fallback codes
      setAllHsCodes([]);
    }
  }, []);

  // Search HS codes - Enhanced with CompactInvoiceForm approach
  const searchHSCodes = async (term) => {
    if (!term) {
      setHsCodes([]);
      return;
    }

    // Show all HS codes if term is very short (like CompactInvoiceForm)
    if (term.length === 1) {
      if (allHsCodes.length > 0) {
        const filtered = allHsCodes.filter(hsCode =>
          hsCode.hs_code?.startsWith(term) ||
          hsCode.description?.toLowerCase().includes(term.toLowerCase())
        ).slice(0, 20);
        setHsCodes(filtered);
        console.log(`📋 Showing ${filtered.length} HS codes starting with "${term}"`);
        return;
      }
    }

    if (term.length < 2) {
      setHsCodes([]);
      return;
    }

    setLoading(true);

    try {
      // Use API service for consistent error handling
      const response = await api.reference.getHSCodes({ search: term, limit: 100 });
      const searchResults = response.data.data || [];

      console.log(`🔍 HS Code search for "${term}":`, searchResults.length, 'results found');

      // Log specific search for debugging
      if (term.includes('8711') || term.toLowerCase().includes('motorcycle')) {
        console.log('🎯 Specific search results:', searchResults.slice(0, 5));
      }

      setHsCodes(searchResults);
      console.log(`✅ Found ${searchResults.length} HS codes for "${term}"`);
    } catch (error) {
      console.error('Failed to search HS codes:', error);
      console.warn('API not available, using fallback HS codes:', error.message);

      // Enhanced fallback with client-side search if we have loaded HS codes
      let fallbackHSCodes = [];

      if (allHsCodes.length > 0) {
        // Search in loaded HS codes (like CompactInvoiceForm)
        const searchLower = term.toLowerCase();
        fallbackHSCodes = allHsCodes.filter(hsCode =>
          hsCode.hs_code?.toLowerCase().includes(searchLower) ||
          hsCode.description?.toLowerCase().includes(searchLower) ||
          hsCode.product_description?.toLowerCase().includes(searchLower)
        ).slice(0, 50); // Limit to 50 results

        console.log(`🔍 Client-side search in ${allHsCodes.length} HS codes found ${fallbackHSCodes.length} results`);
      } else {
        // Use static fallback codes
        fallbackHSCodes = getFallbackHSCodes(term);
      }

      setHsCodes(fallbackHSCodes);
      console.log(`⚠️ Using ${fallbackHSCodes.length} fallback HS codes for "${term}"`);
    } finally {
      setLoading(false);
    }
  };

  // Fallback HS codes for common scenarios
  const getFallbackHSCodes = useCallback((term) => {
    const commonHSCodes = [
      // Live animals and animal products
      { code: '0101.2100', description: 'Live horses, asses, mules and hinnies' },
      { code: '0201.1000', description: 'Carcasses and half-carcasses of bovine animals, fresh or chilled' },

      // Textiles (the problematic category)
      { code: '6001.9990', description: 'Pile fabrics, including "long pile" fabrics and terry fabrics, knitted or crocheted - Of other textile materials: Other' },
      { code: '6002.4000', description: 'Knitted or crocheted fabrics of artificial fibres' },
      { code: '6203.4200', description: 'Men\'s or boys\' trousers, bib and brace overalls, of cotton' },
      { code: '5208.1100', description: 'Plain weave cotton fabrics, weighing not more than 100 g/m²' },

      // Electronics and machinery
      { code: '8471.3000', description: 'Portable automatic data processing machines, weighing not more than 10 kg' },
      { code: '8517.1200', description: 'Telephones for cellular networks or for other wireless networks' },
      { code: '8703.2100', description: 'Motor cars and other motor vehicles principally designed for transport of persons' },

      // Motorcycles and vehicles (requested by user)
      { code: '8711.2090', description: 'Motorcycles (including mopeds) and cycles fitted with an auxiliary motor - Other (50cc-250cc)' },
      { code: '8711.1090', description: 'Motorcycles (including mopeds) and cycles fitted with an auxiliary motor - Other (not exceeding 50cc)' },
      { code: '8711.3090', description: 'Motorcycles (including mopeds) and cycles fitted with an auxiliary motor - Other (250cc-500cc)' },
      { code: '8711.4090', description: 'Motorcycles (including mopeds) and cycles fitted with an auxiliary motor - Other (500cc-800cc)' },
      { code: '8711.5090', description: 'Motorcycles (including mopeds) and cycles fitted with an auxiliary motor - Other (exceeding 800cc)' },

      // Steel and metals
      { code: '7208.1000', description: 'Flat-rolled products of iron or non-alloy steel, in coils' },
      { code: '7213.1000', description: 'Bars and rods, hot-rolled, in irregularly wound coils, of iron or non-alloy steel' },

      // Petroleum and chemicals
      { code: '2710.1200', description: 'Light oils and preparations' },
      { code: '2710.1900', description: 'Other light oils and preparations' },

      // Furniture and miscellaneous
      { code: '9403.1000', description: 'Metal furniture of a kind used in offices' },
      { code: '9403.2000', description: 'Other metal furniture' },

      // Pharmaceuticals
      { code: '3004.9000', description: 'Other medicaments (excluding goods of heading 30.02, 30.05 or 30.06)' },

      // Food products
      { code: '1001.9900', description: 'Other wheat and meslin' },
      { code: '1006.3000', description: 'Semi-milled or wholly milled rice' },

      // Cement and construction
      { code: '2523.2100', description: 'White portland cement' },
      { code: '2523.2900', description: 'Other portland cement' }
    ];

    const filtered = commonHSCodes.filter(code =>
      code.code.toLowerCase().includes(term.toLowerCase()) ||
      code.description.toLowerCase().includes(term.toLowerCase())
    );

    return filtered.slice(0, 10); // Limit to 10 results
  }, []);

  // Client-side UoM options (fallback) - Using reference_data codes
  const getClientSideUoMOptions = useCallback((hsCode) => {
    const isTextile = hsCode && (
      hsCode.startsWith('50') || hsCode.startsWith('51') || hsCode.startsWith('52') ||
      hsCode.startsWith('53') || hsCode.startsWith('54') || hsCode.startsWith('55') ||
      hsCode.startsWith('56') || hsCode.startsWith('57') || hsCode.startsWith('58') ||
      hsCode.startsWith('59') || hsCode.startsWith('60') || hsCode.startsWith('61') ||
      hsCode.startsWith('62') || hsCode.startsWith('63')
    );

    if (isTextile) {
      return {
        validUoMs: ['KG', 'MTR', 'SQM'], // Using reference_data codes
        suggestedUoM: 'KG'
      };
    }

    return {
      validUoMs: ['NOS', 'PCS', 'SET', 'KG', 'LTR'], // Using reference_data codes
      suggestedUoM: 'NOS'
    };
  }, []);

  // Get UoM from FBR API
  const getFbrUoM = useCallback(async (hsCode) => {
    if (!hsCode) return null;

    setLoadingFbrUom(true);
    setFbrUomError(null);

    try {
      console.log(`🔍 Fetching FBR UoM for HS Code: ${hsCode}`);

      // Call FBR API Checker's HS Code to UOM API
      const response = await apiClient.get('/fbr-checker/hs-uom', {
        params: {
          hs_code: hsCode,
          annexure_id: '3' // Default to annexure 3
        }
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const fbrData = response.data.data[0];
        const uomData = {
          uomId: fbrData.uoM_ID,
          description: fbrData.description,
          hsCode: hsCode,
          annexure: response.data.params?.annexure_id || '3',
          source: 'FBR API'
        };

        console.log(`✅ FBR UoM found for ${hsCode}:`, uomData);
        setFbrUomData(uomData);

        // Auto-populate UoM if callback provided
        if (onUoMChangeRef.current && fbrData.description && currentUoMRef.current !== fbrData.description) {
          currentUoMRef.current = fbrData.description;
          onUoMChangeRef.current(fbrData.description);
          toast.success(`✅ UoM auto-populated from FBR: ${fbrData.description}`, {
            id: `fbr-uom-${hsCode}`,
            duration: 4000,
            icon: '🏛️'
          });
        }

        return uomData;
      } else {
        console.log(`⚠️ No FBR UoM data found for HS Code: ${hsCode}`);
        setFbrUomError('No UoM data found in FBR database');
        return null;
      }
    } catch (error) {
      console.error('Error fetching FBR UoM:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch UoM from FBR';
      setFbrUomError(errorMessage);

      // Show user-friendly error message
      if (error.response?.status === 400 && error.response?.data?.action === 'Configure FBR token in profile') {
        toast.error('Please configure your FBR API token in profile settings');
      } else {
        toast.error(`FBR API Error: ${errorMessage}`);
      }

      return null;
    } finally {
      setLoadingFbrUom(false);
    }
  }, []);

  // Load UoM options for selected HS Code (enhanced with FBR integration)
  const loadUoMOptions = useCallback(async (hsCode) => {
    if (!hsCode) return;

    setLoadingUoM(true);

    // First try to get UoM from FBR API
    await getFbrUoM(hsCode);

    // Then load local UoM options as fallback/additional options
    // Safety check for API availability
    if (!api?.reference?.getUoMOptions) {
      console.warn('API getUoMOptions not available, using client-side validation');
      const clientSideOptions = getClientSideUoMOptions(hsCode);
      setUoMOptions(clientSideOptions.validUoMs);
      setLoadingUoM(false);
      return;
    }

    try {
      const response = await api.reference.getUoMOptions(hsCode);
      if (response.data.success) {
        setUoMOptions(response.data.validUoMs);
      }
    } catch (error) {
      console.error('Error loading local UoM options:', error);
      // Fallback to client-side validation
      const clientSideOptions = getClientSideUoMOptions(hsCode);
      setUoMOptions(clientSideOptions.validUoMs);
    } finally {
      setLoadingUoM(false);
    }
  }, [getClientSideUoMOptions, getFbrUoM]);

  // Client-side UoM validation (fallback)
  const getClientSideValidation = useCallback((hsCode, uom) => {
    const isTextile = hsCode && (
      hsCode.startsWith('50') || hsCode.startsWith('51') || hsCode.startsWith('52') ||
      hsCode.startsWith('53') || hsCode.startsWith('54') || hsCode.startsWith('55') ||
      hsCode.startsWith('56') || hsCode.startsWith('57') || hsCode.startsWith('58') ||
      hsCode.startsWith('59') || hsCode.startsWith('60') || hsCode.startsWith('61') ||
      hsCode.startsWith('62') || hsCode.startsWith('63')
    );

    const isNumbersUoM = uom === 'Numbers, pieces, units';

    if (isTextile && isNumbersUoM) {
      return {
        isValid: false,
        error: `UoM "${uom}" is not allowed for textile HS Code ${hsCode}`,
        category: 'Textiles and Fabrics',
        suggestedUoM: 'KG',
        validUoMs: ['KG', 'Meter', 'Square Metre']
      };
    }

    return {
      isValid: true,
      error: null,
      category: isTextile ? 'Textiles and Fabrics' : 'Other Products',
      suggestedUoM: null,
      validUoMs: isTextile ? ['KG', 'Meter', 'Square Metre'] : ['Numbers, pieces, units', 'Pieces', 'Sets', 'KG']
    };
  }, []);

  // Validate UoM for HS Code
  const validateUoM = useCallback(async (hsCode, uom) => {
    if (!hsCode || !uom) return;

    // Safety check for API availability
    if (!api?.reference?.validateUoM) {
      console.warn('API validateUoM not available, using client-side validation');
      const clientSideValidation = getClientSideValidation(hsCode, uom);
      setUoMValidation(clientSideValidation);
      return;
    }

    try {
      const response = await api.reference.validateUoM({ hsCode, uom });
      if (response.data.success) {
        setUoMValidation(response.data.validation);
      }
    } catch (error) {
      console.error('Error validating UoM:', error);
      // Fallback to client-side validation
      const clientSideValidation = getClientSideValidation(hsCode, uom);
      setUoMValidation(clientSideValidation);
    }
  }, [getClientSideValidation]);

  useEffect(() => {
    if (currentHSCode && showUoMValidation) {
      loadUoMOptions(currentHSCode);
    }
  }, [currentHSCode, loadUoMOptions, showUoMValidation]);

  useEffect(() => {
    if (currentHSCode && currentUoM && showUoMValidation) {
      validateUoM(currentHSCode, currentUoM);
    }
  }, [currentHSCode, currentUoM, showUoMValidation, validateUoM]);

  useEffect(() => {
    loadAllHSCodes();
  }, [loadAllHSCodes]);

  // Handle HS Code selection
  const handleHSCodeSelect = (hsCode) => {
    const selectedCode = hsCode.code || hsCode.hs_code || hsCode.hsCode || '';
    const selectedDescription = hsCode.description || hsCode.product_description || hsCode.productDescription || '';

    setSearchTerm(selectedCode);
    setSelectedHSCode(hsCode);
    setShowDropdown(false);

    if (onHSCodeSelect) {
      // Pass the HS code data in the expected format
      onHSCodeSelect({
        code: selectedCode,
        description: selectedDescription,
        hsCode: selectedCode, // For backward compatibility
        productDescription: selectedDescription // For backward compatibility
      });
    }

    // Load UoM options for the selected HS Code
    if (showUoMValidation) {
      loadUoMOptions(selectedCode);
    }
  };

  return (
    <div className="space-y-3">
      {/* HS Code Search */}
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          HS Code
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              searchHSCodes(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => {
              if (hsCodes.length > 0) setShowDropdown(true);
            }}
            placeholder="Search HS Code or description..."
            disabled={disabled}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          {loading && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        {/* HS Code Dropdown */}
        {showDropdown && hsCodes.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {hsCodes.map((hsCode, index) => (
              <div
                key={index}
                onClick={() => handleHSCodeSelect(hsCode)}
                className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm">{hsCode.code || hsCode.hs_code || hsCode.hsCode}</div>
                <div className="text-xs text-gray-600 truncate">{hsCode.description || hsCode.product_description || hsCode.productDescription}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FBR UoM Integration Section */}
      {showUoMValidation && currentHSCode && (
        <div className="space-y-2">
          {/* FBR UoM Status */}
          {(loadingFbrUom || fbrUomData || fbrUomError) && (
            <div className={`p-3 rounded-md border ${fbrUomData
                ? 'bg-blue-50 border-blue-200'
                : fbrUomError
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
              <div className="flex items-start space-x-2">
                {loadingFbrUom ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mt-1"></div>
                ) : fbrUomData ? (
                  <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5" />
                ) : (
                  <InformationCircleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className={`text-sm font-medium ${fbrUomData ? 'text-blue-800' : fbrUomError ? 'text-yellow-800' : 'text-gray-800'
                    }`}>
                    {loadingFbrUom ? 'Fetching FBR UoM...' :
                      fbrUomData ? `✅ FBR UoM: ${fbrUomData.description}` :
                        '⚠️ FBR UoM Not Available'}
                  </div>
                  <div className={`text-xs mt-1 ${fbrUomData ? 'text-blue-700' : fbrUomError ? 'text-yellow-700' : 'text-gray-700'
                    }`}>
                    {loadingFbrUom ? 'Checking FBR database for official UoM...' :
                      fbrUomData ? `Official UoM from FBR Annexure ${fbrUomData.annexure} (ID: ${fbrUomData.uomId})` :
                        fbrUomError || 'FBR API not available - using local validation'}
                  </div>
                  {fbrUomData && (
                    <div className="text-xs text-blue-600 mt-1 font-medium">
                      🏛️ Pakistan FBR Official Database
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}


    </div>
  );
};

export default HSCodeSearchWithUoM;
